export type SpellType = 'fireball' | 'iceShard' | 'shield' | 'lightningBolt' | 'healSpell' | 'reflect' | 'teleport' | 'meteor' | 'superShield' | 'blizzard';

export type UltimateSpellType = 'meteor' | 'superShield' | 'blizzard';

export const ULTIMATE_SPELLS: Set<UltimateSpellType> = new Set(['meteor', 'superShield', 'blizzard']);

export function isUltimateSpell(spell: SpellType): spell is UltimateSpellType {
  return ULTIMATE_SPELLS.has(spell as UltimateSpellType);
}

interface CooldownState {
  lastCast: number;
  cooldownDuration: number;
}

export class CooldownManager {
  private cooldowns: Map<SpellType, CooldownState> = new Map();
  private listeners: Set<() => void> = new Set();
  private slowMultiplier: number = 1; // 1 = normal, > 1 = slowed
  private slowEndTime: number = 0;

  setCooldown(spell: SpellType, duration: number): void {
    // Apply slow effect to cooldown duration
    const effectiveDuration = duration * this.getEffectiveSlowMultiplier();
    this.cooldowns.set(spell, {
      lastCast: Date.now(),
      cooldownDuration: effectiveDuration
    });
    this.notify();
  }

  isReady(spell: SpellType): boolean {
    const state = this.cooldowns.get(spell);
    if (!state) return true;
    return Date.now() - state.lastCast >= state.cooldownDuration;
  }

  getRemainingTime(spell: SpellType): number {
    const state = this.cooldowns.get(spell);
    if (!state) return 0;
    const remaining = state.cooldownDuration - (Date.now() - state.lastCast);
    return Math.max(0, remaining);
  }

  getCooldownProgress(spell: SpellType): number {
    const state = this.cooldowns.get(spell);
    if (!state) return 1;
    const elapsed = Date.now() - state.lastCast;
    return Math.min(1, elapsed / state.cooldownDuration);
  }

  // Apply a slow effect - cooldowns take longer
  applySlow(multiplier: number, durationMs: number): void {
    this.slowMultiplier = multiplier;
    this.slowEndTime = Date.now() + durationMs;
    this.notify();
  }

  // Get current slow multiplier (1 if slow has expired)
  getEffectiveSlowMultiplier(): number {
    if (Date.now() > this.slowEndTime) {
      this.slowMultiplier = 1;
    }
    return this.slowMultiplier;
  }

  isSlowed(): boolean {
    return this.getEffectiveSlowMultiplier() > 1;
  }

  getSlowRemainingTime(): number {
    return Math.max(0, this.slowEndTime - Date.now());
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach(listener => listener());
  }

  reset(): void {
    this.cooldowns.clear();
    this.slowMultiplier = 1;
    this.slowEndTime = 0;
    this.notify();
  }
}
